import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { joinWaitlist, fetchWaitlistStatus } from "@/lib/api";
import { notifAllowed } from "@/lib/notifTiming";
import { Sparkles, Clock, Calculator, Sun, Check, Loader2, Star, X } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  { icon: Clock, en: "Muhurta Finder", hi: "मुहूर्त खोजक", desc_en: "Find the most auspicious windows for any sankalpa.", desc_hi: "किसी भी संकल्प हेतु शुभ मुहूर्त खोजें।" },
  { icon: Calculator, en: "Advanced Calculations", hi: "उन्नत गणनाएँ", desc_en: "Precise Swiss-Ephemeris tithi, yoga, karana & transits.", desc_hi: "सटीक स्विस-एफ़ेमेरिस तिथि, योग, करण एवं गोचर।" },
  { icon: Sun, en: "Choghadiya & Horas", hi: "चौघड़िया एवं होरा", desc_en: "Day & night Choghadiya and planetary Horas at a glance.", desc_hi: "दिन-रात्रि चौघड़िया एवं ग्रह होरा एक दृष्टि में।" },
  { icon: Star, en: "Personalised Panchangam", hi: "वैयक्तिक पंचांग", desc_en: "Your nakshatra, tarabala & chandrabala, saved for you.", desc_hi: "आपका नक्षत्र, ताराबल एवं चन्द्रबल, आपके लिए सुरक्षित।" },
];
const INTERESTS = FEATURES.map((f) => f.en);

// Shared card content: feature grid + waitlist form. `compact` trims it down
// for the popup (short pitch, icon-only feature list, no interest chips).
function PremiumInner({ scope = "", compact = false }) {
  const { pick } = useLang();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (v) => setChosen((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));

  const submit = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error(pick("मान्य ईमेल दर्ज करें", "Enter a valid email")); return; }
    setBusy(true);
    try {
      await joinWaitlist({ email, name, interests: chosen, source: "panchang_premium" });
      try { localStorage.setItem("dh_waitlisted", "1"); } catch (e) { /* ignore */ }
      setDone(true);
      toast(pick("धन्यवाद! आप प्रतीक्षा-सूची में हैं।", "Thank you! You're on the waitlist."));
    } catch (e) {
      toast.error(pick("जोड़ नहीं सके", "Could not join"), { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--gold-muted)] bg-surface/70 px-3 py-1 text-xs font-medium tracking-wide text-gold">
        <Sparkles className="h-3.5 w-3.5" /> {pick("शीघ्र आ रहा है", "Coming soon")}
      </span>
      <h2 className={compact ? "mt-3 font-serif-latin text-xl text-ink" : "mt-4 font-serif-latin text-2xl text-ink md:text-3xl"} data-testid={`${scope}premium-title`}>
        {pick("धर्मार्थ पंचांग प्रीमियम", "Dharmartha Panchangam Premium")}
      </h2>
      <p className={compact ? "mt-1.5 text-sm leading-relaxed text-ink-2" : "mt-2 max-w-2xl text-[15px] leading-relaxed text-ink-2"}>
        {compact
          ? pick("मुहूर्त, उन्नत गणनाएँ, चौघड़िया व होरा — प्रतीक्षा-सूची में जुड़ें।", "Muhurta, advanced calculations, Choghadiya & Horas. Join the waitlist.")
          : pick(
            "एक गहन ज्योतिषीय टूलकिट — मुहूर्त खोजक, उन्नत गणनाएँ, चौघड़िया, होरा और भी बहुत कुछ। प्रतीक्षा-सूची में जुड़ें।",
            "A deeper jyotisha toolkit — Muhurta Finder, advanced calculations, Choghadiya, Horas and much more. Join the waitlist."
          )}
      </p>

      {compact ? (
        <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
          {FEATURES.map((f) => (
            <li key={f.en} className="flex items-center gap-2 text-[13px] text-ink-2">
              <f.icon className="h-3.5 w-3.5 shrink-0 text-gold" /> {pick(f.hi, f.en)}
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <div key={f.en} className="flex items-start gap-3 border border-line bg-surface/70 p-3.5">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center border border-[var(--gold-muted)] text-gold"><f.icon className="h-4 w-4" /></span>
              <div>
                <p className="font-serif-latin text-[15px] text-ink">{pick(f.hi, f.en)}</p>
                <p className="mt-0.5 text-xs text-ink-3">{pick(f.desc_hi, f.desc_en)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {done ? (
        <div className={`${compact ? "mt-4 p-3.5" : "mt-6 p-4"} flex items-center gap-3 border border-[var(--gold-muted)] bg-surface/80`} data-testid={`${scope}premium-waitlist-done`}>
          <Check className="h-6 w-6 shrink-0 text-saffron" />
          <div>
            <p className="font-serif-latin text-lg text-ink">{pick("आप सूची में हैं!", "You're on the list!")}</p>
            <p className="text-sm text-ink-3">{pick("प्रीमियम आरंभ होते ही हम आपको सूचित करेंगे।", "We'll notify you the moment Premium launches.")}</p>
          </div>
        </div>
      ) : (
        <div className={compact ? "mt-4" : "mt-6"} data-testid={`${scope}premium-waitlist-form`}>
          {!compact && <p className="mb-2 overline-label text-gold">{pick("प्रतीक्षा-सूची में जुड़ें", "Join the waitlist")}</p>}
          {!compact && (
            <div className="mb-3 flex flex-wrap gap-2">
              {INTERESTS.map((it, i) => {
                const on = chosen.includes(it);
                return (
                  <button key={it} type="button" onClick={() => toggle(it)} data-testid={`${scope}premium-interest-${i}`}
                    className={`border px-2.5 py-1.5 text-xs transition-colors ${on ? "border-saffron bg-saffron text-white" : "border-line text-ink-2 hover:border-saffron"}`}>
                    {pick(FEATURES[i].hi, it)}
                  </button>
                );
              })}
            </div>
          )}
          <div className={compact ? "flex flex-col gap-2.5 sm:flex-row" : "grid gap-2.5 sm:grid-cols-2"}>
            {!compact && (
              <input data-testid={`${scope}premium-name`} value={name} onChange={(e) => setName(e.target.value)} placeholder={pick("आपका नाम (वैकल्पिक)", "Your name (optional)")}
                className="w-full border border-line bg-parchment px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none" />
            )}
            <input data-testid={`${scope}premium-email`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={pick("ईमेल पता", "Email address")}
              className="w-full border border-line bg-parchment px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none" />
            {compact && (
              <button data-testid={`${scope}premium-waitlist-submit`} onClick={submit} disabled={busy}
                className="inline-flex shrink-0 items-center justify-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {pick("सूचित करें", "Notify me")}
              </button>
            )}
          </div>
          {!compact && (
            <button data-testid={`${scope}premium-waitlist-submit`} onClick={submit} disabled={busy}
              className="mt-3 inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {pick("मुझे सूचित करें", "Notify me")}
            </button>
          )}
        </div>
      )}
    </>
  );
}

// Popup shown on the Panchang page, but:
//  • NEVER to anyone who has already joined the waitlist (localStorage flag set on
//    submit, plus a cross-device check via /waitlist/status for logged-in users), and
//  • at most ONCE PER SESSION for everyone else (sessionStorage guard).
// Rendered through a portal to document.body so it escapes the <main> stacking
// context and always sits ABOVE the site header.
const WAITLISTED_KEY = "dh_waitlisted";
const SESSION_SEEN_KEY = "dh_panchang_popup_seen";

export function PanchangPremiumModal() {
  const { pick } = useLang();
  const { system_messages } = useSettings();
  const popupEnabled = ((system_messages || {}).popup_panchang || {}).enabled !== false
    && notifAllowed(system_messages, "popup_panchang");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const safeGet = (store, key) => { try { return store.getItem(key); } catch (e) { return null; } };

    const maybeShow = async () => {
      if (!popupEnabled) return;
      // Already waitlisted on this browser → never show again.
      if (safeGet(localStorage, WAITLISTED_KEY) === "1") return;
      // Already shown once this session → don't show again.
      if (safeGet(sessionStorage, SESSION_SEEN_KEY) === "1") return;
      // Cross-device check for logged-in users; cache the result locally.
      try {
        const { waitlisted } = await fetchWaitlistStatus();
        if (waitlisted) { try { localStorage.setItem(WAITLISTED_KEY, "1"); } catch (e) { /* ignore */ } return; }
      } catch (e) { /* not logged in / ignore */ }
      if (cancelled) return;
      // Give the visitor time to read the Panchang before inviting them.
      timer = setTimeout(() => {
        if (cancelled) return;
        try { sessionStorage.setItem(SESSION_SEEN_KEY, "1"); } catch (e) { /* ignore */ }
        setOpen(true);
      }, 10000);
    };

    maybeShow();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [popupEnabled]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [open]);

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="panchang-premium">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-md overflow-hidden border border-[var(--gold-muted)] bg-surface shadow-2xl">
        <button onClick={() => setOpen(false)} data-testid="premium-close" aria-label="Close"
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-line bg-surface/80 text-ink-2 transition-colors hover:bg-parchment hover:text-ink">
          <X className="h-4 w-4" />
        </button>
        <div className="max-h-[85vh] overflow-y-auto p-6">
          <PremiumInner compact />
          <button onClick={() => setOpen(false)} data-testid="premium-dismiss"
            className="mt-4 text-xs text-ink-3 underline-offset-2 transition-colors hover:text-ink hover:underline">
            {pick("अभी नहीं", "Maybe later")}
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}

// Inline section (kept on the Panchang page as well).
export default function PanchangPremium() {
  return (
    <section className="mx-auto max-w-5xl px-5 pb-16 md:px-8" data-testid="panchang-premium-inline">
      <div className="relative overflow-hidden border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.06)] p-7 md:p-10">
        <PremiumInner scope="inline-" />
      </div>
    </section>
  );
}
